import { createRef, PureComponent } from "react";

import { cn } from "@btnet/utilities-core";

import { friendlyDefaultDisplayValue } from "utilities/helpers";

import { BTFormItemAutomatic } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTIconEyeglasses } from "commonComponents/btWrappers/BTIcon";
import { BTPopover } from "commonComponents/btWrappers/BTPopover/BTPopover";
import { BTTextArea } from "commonComponents/btWrappers/BTTextArea/BTTextArea";
import { type IGridCellRenderProps } from "commonComponents/utilities/Grid/Grid.types";
import {
    type IPreviewLineItemProps,
    LineItemPreview,
} from "commonComponents/utilities/LineItemContainer/LineItemPreview";
import { ValueDisplay } from "commonComponents/utilities/ValueDisplay/ValueDisplay";

import "./CellImplementations.less";

export interface ITextAreaCellProps extends IPreviewLineItemProps {
    text?: string;
    isChildOfSubCategory?: boolean;
    previewKey?: string;
}

interface ITextAreaCellState {
    isOverflowed: boolean;
}

export class TextAreaCell extends PureComponent<IGridCellRenderProps<ITextAreaCellProps>> {
    private divRef = createRef<HTMLDivElement>();

    state: Readonly<ITextAreaCellState> = {
        isOverflowed: false,
    };

    setOverflow() {
        if (this.divRef?.current) {
            const state = {
                isOverflowed: this.divRef.current.scrollHeight > this.divRef.current.clientHeight,
            };
            this.setState(state);
        }
    }

    componentDidMount() {
        this.setOverflow();
    }

    componentDidUpdate() {
        this.setOverflow();
    }

    render() {
        const { cellValue, isInEdit, field, onBlur, onChange, canEdit, autoFocus, onFocus } =
            this.props;

        if (isInEdit && canEdit) {
            return (
                <LineItemPreview
                    fieldName="textarea"
                    addPreviewListener={cellValue?.addPreviewListener}
                    render={(preview) => (
                        <BTFormItemAutomatic id={field.full}>
                            <BTTextArea<undefined>
                                id={field.full}
                                data-testid={`${field.full}-text`}
                                value={
                                    (preview?.[cellValue?.previewKey ?? ""] as
                                        | string
                                        | undefined) ?? cellValue?.text
                                }
                                maxRows={5}
                                onBlur={onBlur}
                                onChange={onChange}
                                autoFocus={autoFocus}
                                className={preview ? "LineItemPreview" : undefined}
                            />
                        </BTFormItemAutomatic>
                    )}
                />
            );
        } else {
            return (
                <div
                    className={cn({
                        lineItemEditableCell: canEdit,
                        isChildOfSubCategory: cellValue?.isChildOfSubCategory,
                    })}
                    tabIndex={0}
                    onFocus={onFocus}
                >
                    <ValueDisplay
                        data-testid={`${field.full}`}
                        ref={this.divRef}
                        value={cellValue?.text || friendlyDefaultDisplayValue}
                        className="textAreaCell"
                    />
                    {this.state.isOverflowed && (
                        <BTPopover
                            content={cellValue?.text || friendlyDefaultDisplayValue}
                            placement="topLeft"
                            widthType="unrestricted"
                            overlayClassName="gridCellPopover"
                        >
                            <BTIconEyeglasses
                                data-testid={`${field.full}-eyeglassicon`}
                                className="gridCellIcon"
                                size={16}
                            />
                        </BTPopover>
                    )}
                </div>
            );
        }
    }
}
